<template>
  <section class="rule-section" aria-label="赋分模型计算标准">
    <div class="section-toolbar">
      <el-radio-group :value="stage" size="medium" @input="requestStage">
        <el-radio-button v-for="item in stages" :key="item.value" :label="item.value">
          {{ item.label }}
          <span v-if="stageDirty(item.value)" class="dirty-dot" aria-label="有未保存修改"></span>
        </el-radio-button>
      </el-radio-group>
      <el-button type="primary" icon="el-icon-check" :loading="saving"
        :disabled="!stageDirty(stage) || loading" @click="save">保存{{ stageLabel }}标准</el-button>
    </div>

    <div v-if="indicatorRows.length" class="score-summary" aria-live="polite">
      <span v-for="item in scoreSummaries" :key="item.key"
        :class="['score-summary-item', { invalid: !item.valid }]">
        {{ item.label }} <strong>{{ formatMaxScore(item.value) }} / {{ formatMaxScore(item.expected) }}</strong>
        <i :class="item.valid ? 'el-icon-success' : 'el-icon-warning'"></i>
      </span>
    </div>

    <el-alert v-if="currentError" :title="currentError" type="error" show-icon :closable="false">
      <el-button slot="default" size="mini" @click="loadStage(stage, true)">重新加载</el-button>
    </el-alert>

    <el-table v-else v-loading="loading" :data="indicatorRows" :span-method="spanSummaryColumns"
      border stripe row-key="indicatorCode" empty-text="暂无赋分标准数据">
      <el-table-column prop="primaryType" label="一类指标" width="110" />
      <el-table-column prop="indicatorCategory" label="指标类别" min-width="140" />
      <el-table-column prop="indicatorName" label="指标名称" min-width="240" />
      <el-table-column label="单项满分" width="100" align="center">
        <template slot-scope="scope">{{ formatScore(scope.row.maxScore) }}</template>
      </el-table-column>
      <el-table-column label="计算标准" min-width="330">
        <template slot-scope="scope">
          <div class="standard-summary">
            <div v-for="interval in scope.row.intervals" :key="interval.rowKey" class="standard-line">
              <span>{{ rangeText(interval) }}，{{ formatScore(interval.score) }}分；</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="92" align="center">
        <template slot-scope="scope">
          <el-button type="text" icon="el-icon-edit" @click="openEditor(scope.row)">修改</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :visible.sync="dialogVisible" :title="dialogTitle" width="1120px" top="6vh"
      append-to-body :close-on-click-modal="false" @closed="clearDialog">
      <div v-if="dialogDraft.length" class="dialog-content">
        <div class="dialog-meta">
          <div><span>企业阶段</span><strong>{{ stageLabel }}</strong></div>
          <label class="max-score-field">
            <span>单项满分</span>
            <el-input-number v-model="dialogMaxScoreDraft" size="small" controls-position="right"
              :min="0" :max="100" :precision="2" :step="1" />
          </label>
          <label class="unit-field">
            <span>统一单位</span>
            <el-input :value="dialogUnit" size="small" maxlength="20" placeholder="请输入单位"
              @input="updateDialogUnit" />
          </label>
        </div>

        <el-table :data="dialogDraft" border stripe row-key="rowKey" class="interval-table">
          <el-table-column type="index" label="序号" width="54" align="center" />
          <el-table-column label="下限" min-width="165">
            <template slot-scope="scope">
              <div class="boundary-editor">
                <el-checkbox v-if="scope.$index === 0 && allowsNoLower" v-model="scope.row.noLower"
                  @change="toggleLowerInfinity(scope.row)">无下限</el-checkbox>
                <div v-if="!scope.row.noLower" class="finite-boundary">
                  <el-input-number v-model="scope.row.lowerBound" size="mini" controls-position="right"
                    :precision="2" :min="isNonNegativeIndicator ? 0 : undefined"
                    :disabled="scope.$index === 0 && isNonNegativeIndicator"
                    @change="syncLowerBoundary(scope.$index)" />
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="上限" min-width="165">
            <template slot-scope="scope">
              <div class="boundary-editor">
                <el-checkbox v-if="scope.$index === dialogDraft.length - 1" v-model="scope.row.noUpper"
                  @change="toggleUpperInfinity(scope.row)">无上限</el-checkbox>
                <div v-if="!scope.row.noUpper" class="finite-boundary">
                  <el-input-number v-model="scope.row.upperBound" size="mini" controls-position="right"
                    :precision="2" @change="syncUpperBoundary(scope.$index)" />
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="区间预览" min-width="210">
            <template slot-scope="scope">{{ rangeText(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="区间分值" width="180" align="center">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.score" class="score-input" :min="0" :max="Number(scope.row.maxScore)"
                :precision="4" :step="1" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="92" align="center">
            <template slot-scope="scope">
              <el-tooltip content="拆分当前区间" placement="top">
                <el-button type="text" icon="el-icon-circle-plus-outline"
                  aria-label="拆分当前区间" @click="splitDialogInterval(scope.$index)" />
              </el-tooltip>
              <el-tooltip content="删除当前区间" placement="top">
                <el-button type="text" class="danger-action" icon="el-icon-delete"
                  :disabled="dialogDraft.length <= 1" aria-label="删除当前区间"
                  @click="removeDialogInterval(scope.$index)" />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="applyDialog">确定</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
const API = 'apij2/admin-api/trade/enterprise-innovation-point-rule';
const ZERO_BASED_INDICATOR_CODES = [
  'RD_EXPENSE_AMOUNT',
  'RD_EXPENSE_RATIO',
  'RD_PERSONNEL_RATIO',
  'INVENTION_PATENT_GRANTED',
  'IP_PER_100_RD_PERSONNEL',
  'TECH_CONTRACT_AMOUNT',
  'MAIN_BUSINESS_REVENUE',
  'REVENUE_GROWTH_RATE',
  'ROE'
];
const SCORE_GROUPS = [
  {
    key: 'innovation-input',
    label: '创新投入',
    expected: 50,
    codes: ['RD_EXPENSE_AMOUNT', 'RD_EXPENSE_RATIO', 'RD_PERSONNEL_RATIO']
  },
  {
    key: 'innovation-output',
    label: '创新产出',
    expected: 30,
    codes: ['INVENTION_PATENT_GRANTED', 'IP_PER_100_RD_PERSONNEL', 'TECH_CONTRACT_AMOUNT']
  },
  {
    key: 'innovation-development',
    label: '创新发展',
    expected: 20,
    codes: ['MAIN_BUSINESS_REVENUE', 'REVENUE_GROWTH_RATE', 'ROE']
  }
];

export default {
  name: 'ScoreStandardConfig',
  data() {
    return {
      stage: 'STARTUP',
      stages: [
        { value: 'STARTUP', label: '初创期' },
        { value: 'GROWTH', label: '成长期' },
        { value: 'STABLE', label: '稳定期' }
      ],
      cache: {},
      originals: {},
      errors: {},
      loading: false,
      saving: false,
      tempSequence: 0,
      dialogVisible: false,
      dialogIndicator: null,
      dialogMaxScoreDraft: null,
      dialogDraft: []
    };
  },
  computed: {
    rows() { return this.cache[this.stage] || []; },
    indicatorRows() {
      const groups = [];
      const byCode = {};
      this.rows.forEach(row => {
        if (!byCode[row.indicatorCode]) {
          byCode[row.indicatorCode] = {
            indicatorCode: row.indicatorCode,
            primaryType: row.primaryType,
            indicatorCategory: row.indicatorCategory,
            indicatorName: row.indicatorName,
            maxScore: row.maxScore,
            intervals: []
          };
          groups.push(byCode[row.indicatorCode]);
        }
        byCode[row.indicatorCode].intervals.push(row);
      });
      return groups;
    },
    currentError() { return this.errors[this.stage] || ''; },
    scoreSummaries() {
      const total = this.indicatorRows.reduce((sum, row) => sum + Number(row.maxScore || 0), 0);
      return [
        { key: 'total', label: `${this.stageLabel}总分`, value: total, expected: 100,
          valid: Math.abs(total - 100) < 0.000001 },
        ...SCORE_GROUPS.map(group => {
          const value = this.indicatorRows.filter(row => group.codes.includes(row.indicatorCode))
            .reduce((sum, row) => sum + Number(row.maxScore || 0), 0);
          return { ...group, value, valid: Math.abs(value - group.expected) < 0.000001 };
        })
      ];
    },
    stageLabel() {
      const item = this.stages.find(stage => stage.value === this.stage);
      return item ? item.label : '';
    },
    dialogTitle() {
      return this.dialogIndicator ? `修改 - ${this.dialogIndicator.indicatorName}` : '修改计算标准';
    },
    dialogUnit() {
      return this.dialogDraft.length ? this.dialogDraft[0].unit : '';
    },
    isNonNegativeIndicator() {
      return Boolean(this.dialogIndicator
        && this.isNonNegativeIndicatorCode(this.dialogIndicator.indicatorCode));
    },
    allowsNoLower() {
      return !this.isNonNegativeIndicator;
    }
  },
  created() { this.loadStage(this.stage); },
  methods: {
    isNonNegativeIndicatorCode(code) {
      return ZERO_BASED_INDICATOR_CODES.includes(code);
    },
    requestStage(nextStage) {
      if (nextStage === this.stage) return;
      this.stage = nextStage;
      this.loadStage(nextStage);
    },
    async loadStage(stage, force) {
      if (this.cache[stage] && !force) return;
      this.loading = true;
      this.$set(this.errors, stage, '');
      try {
        const res = await this.http.get(`${this.urlConfig.devUrl}${API}/score-standards?stage=${stage}`, true);
        const rows = (res.data || []).map(row => ({
          ...row,
          rowKey: `id-${row.id}`,
          score: Number(row.score),
          maxScore: Number(row.maxScore),
          lowerBound: row.lowerBound === null ? null : Number(row.lowerBound),
          upperBound: row.upperBound === null ? null : Number(row.upperBound),
          lowerInclusive: Boolean(row.lowerInclusive),
          upperInclusive: Boolean(row.upperInclusive),
          noLower: row.lowerBound === null,
          noUpper: row.upperBound === null
        }));
        this.$set(this.cache, stage, rows);
        this.$set(this.originals, stage, this.snapshot(stage));
      } catch (error) {
        this.$set(this.errors, stage, (error && (error.msg || error.status)) || '赋分标准加载失败');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      const maximumValidationMessage = this.validateStageMaximums();
      if (maximumValidationMessage) {
        this.$message.warning(maximumValidationMessage);
        return;
      }
      const validationMessage = this.validateRows(this.rows);
      if (validationMessage) {
        this.$message.warning(validationMessage);
        return;
      }
      this.saving = true;
      try {
        await this.http.post(`${this.urlConfig.devUrl}${API}/score-standards/save`, {
          stage: this.stage,
          maxScores: this.indicatorRows.map(row => ({
            indicatorCode: row.indicatorCode,
            maxScore: row.maxScore
          })),
          items: this.rows.map(row => ({
            id: row.id || null,
            indicatorCode: row.indicatorCode,
            lowerBound: row.noLower ? null : row.lowerBound,
            upperBound: row.noUpper ? null : row.upperBound,
            lowerInclusive: row.noLower ? false : row.lowerInclusive,
            upperInclusive: row.noUpper ? false : row.upperInclusive,
            unit: row.unit,
            score: row.score
          }))
        }, true);
        await this.loadStage(this.stage, true);
        this.$message.success(`${this.stageLabel}赋分标准已保存`);
      } finally {
        this.saving = false;
      }
    },
    openEditor(indicator) {
      this.dialogIndicator = indicator;
      this.dialogMaxScoreDraft = Number(indicator.maxScore);
      this.dialogDraft = indicator.intervals.map(row => ({ ...row }));
      this.normalizeDialogEdges();
      this.dialogVisible = true;
    },
    applyDialog() {
      const maximum = Number(this.dialogMaxScoreDraft);
      if (!Number.isFinite(maximum) || maximum < 0) {
        this.$message.warning('单项满分不能为空或小于0');
        return;
      }
      const validationMessage = this.validateIndicatorRows(this.dialogDraft, maximum);
      if (validationMessage) {
        this.$message.warning(validationMessage);
        return;
      }
      const code = this.dialogIndicator.indicatorCode;
      const start = this.rows.findIndex(row => row.indicatorCode === code);
      const count = this.rows.filter(row => row.indicatorCode === code).length;
      this.rows.splice(start, count, ...this.dialogDraft.map(row => ({ ...row, maxScore: maximum })));
      this.dialogVisible = false;
    },
    clearDialog() {
      this.dialogIndicator = null;
      this.dialogMaxScoreDraft = null;
      this.dialogDraft = [];
    },
    updateDialogUnit(value) {
      this.dialogDraft.forEach(row => { row.unit = value; });
    },
    toggleLowerInfinity(row) {
      if (row.noLower) {
        row.lowerBound = null;
        row.lowerInclusive = false;
      } else if (row.lowerBound === null) {
        row.lowerBound = row.upperBound === null ? 0 : Number((row.upperBound - 1).toFixed(4));
        row.lowerInclusive = true;
      }
    },
    toggleUpperInfinity(row) {
      if (row.noUpper) {
        row.upperBound = null;
        row.upperInclusive = false;
      } else if (row.upperBound === null) {
        row.upperBound = row.lowerBound === null ? 1 : Number((row.lowerBound + 1).toFixed(4));
        row.upperInclusive = false;
      }
    },
    syncLowerBoundary(index) {
      if (index <= 0) return;
      const current = this.dialogDraft[index];
      const previous = this.dialogDraft[index - 1];
      current.noLower = false;
      current.lowerInclusive = true;
      previous.noUpper = false;
      previous.upperBound = current.lowerBound;
      previous.upperInclusive = false;
    },
    syncUpperBoundary(index) {
      if (index >= this.dialogDraft.length - 1) return;
      const current = this.dialogDraft[index];
      const next = this.dialogDraft[index + 1];
      current.noUpper = false;
      current.upperInclusive = false;
      next.noLower = false;
      next.lowerBound = current.upperBound;
      next.lowerInclusive = true;
    },
    splitDialogInterval(index) {
      const row = this.dialogDraft[index];
      const lower = row.noLower ? null : Number(row.lowerBound);
      const upper = row.noUpper ? null : Number(row.upperBound);
      if (lower === null && upper === null) {
        this.$message.warning('请先为当前区间设置一个有限边界');
        return;
      }
      const split = Number((lower === null ? upper - 1
        : (upper === null ? lower + 1 : lower + (upper - lower) / 2)).toFixed(4));
      if ((lower !== null && split <= lower) || (upper !== null && split >= upper)) {
        this.$message.warning('当前区间过小，无法按4位小数精度拆分');
        return;
      }
      const oldUpper = row.upperBound;
      const oldNoUpper = row.noUpper;
      row.upperBound = split;
      row.upperInclusive = false;
      row.noUpper = false;
      const added = {
        ...row,
        id: null,
        intervalCode: null,
        rowKey: `temp-${++this.tempSequence}`,
        lowerBound: split,
        lowerInclusive: true,
        noLower: false,
        upperBound: oldUpper,
        upperInclusive: false,
        noUpper: oldNoUpper
      };
      this.dialogDraft.splice(index + 1, 0, added);
    },
    removeDialogInterval(index) {
      if (this.dialogDraft.length <= 1) return;
      const removed = this.dialogDraft[index];
      if (index === 0) {
        const next = this.dialogDraft[1];
        next.lowerBound = removed.lowerBound;
        next.lowerInclusive = removed.lowerInclusive;
        next.noLower = removed.noLower;
      } else {
        const previous = this.dialogDraft[index - 1];
        previous.upperBound = removed.upperBound;
        previous.upperInclusive = removed.upperInclusive;
        previous.noUpper = removed.noUpper;
      }
      this.dialogDraft.splice(index, 1);
      this.normalizeDialogEdges();
    },
    normalizeDialogEdges() {
      this.dialogDraft.forEach((row, index) => {
        if (index > 0) row.noLower = false;
        if (index < this.dialogDraft.length - 1) row.noUpper = false;
        row.lowerInclusive = row.noLower ? false : true;
        row.upperInclusive = false;
      });
      if (this.isNonNegativeIndicator && this.dialogDraft.length) {
        this.dialogDraft[0].noLower = false;
        this.dialogDraft[0].lowerBound = 0;
        this.dialogDraft[0].lowerInclusive = true;
      }
    },
    validateRows(rows) {
      const groups = {};
      rows.forEach(row => {
        if (!groups[row.indicatorCode]) groups[row.indicatorCode] = [];
        groups[row.indicatorCode].push(row);
      });
      for (const code of Object.keys(groups)) {
        const message = this.validateIndicatorRows(groups[code]);
        if (message) return message;
      }
      return '';
    },
    validateStageMaximums() {
      if (this.indicatorRows.length !== 9) return '必须配置全部9个核心指标的单项满分';
      const invalid = this.indicatorRows.find(row => !Number.isFinite(Number(row.maxScore))
        || Number(row.maxScore) < 0);
      if (invalid) return `${invalid.indicatorName}的单项满分不能为空或小于0`;
      const total = this.scoreSummaries[0];
      if (!total.valid) return `${this.stageLabel}9个单项满分合计必须等于100，当前为${this.formatMaxScore(total.value)}`;
      const invalidGroup = this.scoreSummaries.slice(1).find(item => !item.valid);
      if (invalidGroup) {
        return `${this.stageLabel}${invalidGroup.label}单项满分合计必须等于${this.formatMaxScore(invalidGroup.expected)}`
          + `，当前为${this.formatMaxScore(invalidGroup.value)}`;
      }
      return '';
    },
    validateIndicatorRows(rows, maximumValue) {
      if (!rows.length) return '每个指标至少需要一个计算区间';
      const name = rows[0].indicatorName;
      const unit = rows[0].unit;
      const maximum = maximumValue === undefined ? Number(rows[0].maxScore) : Number(maximumValue);
      if (this.isNonNegativeIndicatorCode(rows[0].indicatorCode)
          && (rows[0].noLower || Number(rows[0].lowerBound) !== 0)) {
        return `${name}的首个区间必须从0开始`;
      }
      let previous = null;
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const score = Number(row.score);
        if (!unit || row.unit !== unit) return `${name}的区间单位必须统一且不能为空`;
        if (index > 0 && row.noLower) return `${name}只有第一个区间可以设置无下限`;
        if (index < rows.length - 1 && row.noUpper) return `${name}只有最后一个区间可以设置无上限`;
        if (!row.noLower && !Number.isFinite(Number(row.lowerBound))) return `${name}存在无效下限`;
        if (!row.noUpper && !Number.isFinite(Number(row.upperBound))) return `${name}存在无效上限`;
        if (!row.noLower && !row.lowerInclusive) return `${name}的有限下限必须包含边界值`;
        if (!row.noUpper && row.upperInclusive) return `${name}的有限上限不能包含边界值`;
        if (!row.noLower && !row.noUpper && Number(row.lowerBound) >= Number(row.upperBound)) {
          return `${name}的区间下限必须小于上限`;
        }
        if (!Number.isFinite(score) || score < 0) return `${name}存在无效分值`;
        if (score > maximum + 0.000001) return `${name}的“${this.rangeText(row)}”分值超过单项满分${this.formatMaxScore(maximum)}`;
        if (previous) {
          const comparison = Number(previous.upperBound) - Number(row.lowerBound);
          if (previous.noUpper || row.noLower || comparison > 0
              || (comparison === 0 && previous.upperInclusive && row.lowerInclusive)) {
            return `${name}的计算区间存在重叠`;
          }
          if (comparison < 0 || (comparison === 0 && !previous.upperInclusive && !row.lowerInclusive)) {
            return `${name}的计算区间存在空档`;
          }
          if (score < Number(previous.score)) return `${name}的区间分值必须随区间递增`;
        }
        previous = row;
      }
      return '';
    },
    rangeText(row) {
      const unit = row.unit || '';
      if (row.noLower) {
        return `${this.formatNumber(row.upperBound)}${unit}${row.upperInclusive ? '（含）' : ''}以下`;
      }
      const lower = this.formatLowerBoundary(row.lowerBound, unit);
      if (row.noUpper) {
        return `${lower}${row.lowerInclusive ? '（含）' : ''}以上`;
      }
      return `${lower}${row.lowerInclusive ? '（含）' : ''}`
        + `—${this.formatNumber(row.upperBound)}${unit}${row.upperInclusive ? '（含）' : ''}`;
    },
    formatLowerBoundary(value, unit) {
      return Number(value) === 0 ? this.formatNumber(value) : `${this.formatNumber(value)}${unit}`;
    },
    formatNumber(value) { return Number(value).toFixed(2).replace(/\.?0+$/, ''); },
    formatScore(value) { return Number(value || 0).toFixed(2).replace(/\.00$/, ''); },
    formatMaxScore(value) { return Number(value || 0).toFixed(2); },
    snapshot(stage) {
      return JSON.stringify((this.cache[stage] || []).map(row => [row.id, row.indicatorCode, row.lowerBound,
        row.upperBound, row.lowerInclusive, row.upperInclusive, row.unit, row.score, row.maxScore]));
    },
    stageDirty(stage) { return Boolean(this.originals[stage]) && this.snapshot(stage) !== this.originals[stage]; },
    hasChanges() { return this.stages.some(item => this.stageDirty(item.value)); },
    spanSummaryColumns({ rowIndex, columnIndex }) {
      const property = ['primaryType', 'indicatorCategory'][columnIndex];
      if (!property) return;
      const current = this.indicatorRows[rowIndex][property];
      if (rowIndex > 0 && this.indicatorRows[rowIndex - 1][property] === current) return [0, 0];
      let rowspan = 1;
      while (this.indicatorRows[rowIndex + rowspan]
          && this.indicatorRows[rowIndex + rowspan][property] === current) rowspan += 1;
      return [rowspan, 1];
    }
  }
};
</script>

<style lang="scss" scoped>
.section-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.dirty-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 5px;
  vertical-align: middle;
  background: #e6a23c;
  border-radius: 50%;
}

.standard-summary { padding: 5px 0; }
.score-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 24px;
  min-height: 36px;
  margin: -4px 0 16px;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
  color: #606266;
}
.score-summary-item { display: inline-flex; align-items: center; gap: 6px; }
.score-summary-item strong { color: #303133; }
.score-summary-item i { color: #67c23a; }
.score-summary-item.invalid strong,
.score-summary-item.invalid i { color: #e6a23c; }
.standard-line {
  display: flex;
  align-items: baseline;
  min-height: 24px;
  line-height: 24px;
}

.dialog-meta {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.dialog-meta > div,
.max-score-field,
.unit-field { display: flex; align-items: center; gap: 10px; }
.dialog-meta span { color: #909399; }
.dialog-meta strong { color: #303133; }
.unit-field { margin-left: auto; }
.max-score-field .el-input-number { width: 130px; }
.unit-field .el-input { width: 120px; }

.interval-table { margin-top: 14px; }
.boundary-editor { display: flex; align-items: center; gap: 8px; min-height: 32px; }
.finite-boundary { display: flex; align-items: center; gap: 6px; }
.danger-action { color: #f56c6c; }

::v-deep .interval-table .el-input-number { width: 118px; }
::v-deep .interval-table .score-input { width: 150px; }
::v-deep .el-alert { margin-bottom: 16px; }
::v-deep .el-dialog__body { max-height: 68vh; overflow-y: auto; padding-top: 14px; }

@media (max-width: 1100px) {
  ::v-deep .el-dialog { width: 92% !important; }
  .dialog-meta { flex-wrap: wrap; gap: 12px 24px; }
  .unit-field { margin-left: 0; }
}
</style>
